import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { SkatteverketReceiptVATRequirementsUrl } from "utils/constants/receiptConstants";
//#endregion
//#region components
import { Box, IconButton } from "@mui/material";
import { StaticInfoMessage, ActionLinkButton, Icon } from "components/common";
import cssExports from "theme/styles/main.module.scss";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import useHighReceiptAmountWarning from "./useHighReceiptAmountWarning";

interface HighReceiptAmountBannerProps {
  amount?: number;
  currency?: string;
  categoryType?: number;
  receiptCategoryId?: number;
  date?: Date;
}

const HighReceiptAmountBanner: React.FC<HighReceiptAmountBannerProps> = ({
  amount,
  currency,
  categoryType,
  receiptCategoryId,
  date,
}) => {
  const resolvedCategoryType = useSelector((store: AppState) => {
    if (categoryType !== undefined) return categoryType;
    if (receiptCategoryId === undefined) return undefined;
    const match = store.auth.selectedCompany.receiptCategories?.find((c) => c.id === receiptCategoryId);
    return match?.type;
  });

  const hasCategory = categoryType !== undefined || receiptCategoryId !== undefined;
  const shouldShow = useHighReceiptAmountWarning(amount, currency, resolvedCategoryType, hasCategory, date);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    if (!shouldShow) setDismissed(false);
  }, [shouldShow]);

  if (!shouldShow || dismissed) return null;

  const openSkatteverket = () =>
    window.open(SkatteverketReceiptVATRequirementsUrl, "_blank", "noopener,noreferrer");

  return (
    <Box position="relative">
      <StaticInfoMessage displayType={StyleEnums.DisplayType.Info} gutterBottom>
        <Box pr={4}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.HighAmountVatWarning)}
        </Box>
        <Box mt={1}>
          <ActionLinkButton className={cssExports.animatedLink} onClick={openSkatteverket}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.HighAmountVatWarningLearnMore)}
            <Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={StyleEnums.DisplayType.Info} />
          </ActionLinkButton>
        </Box>
      </StaticInfoMessage>
      <IconButton
        size="small"
        sx={{ position: "absolute", top: 4, right: 4 }}
        onClick={() => setDismissed(true)}
        aria-label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.HighAmountVatWarningCloseAria)}
      >
        <Icon icon={IconDefinitionConstants.WTimes} />
      </IconButton>
    </Box>
  );
};

export default HighReceiptAmountBanner;
